import { chain, difference, get, isNil, startCase } from "lodash";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useCrudEditContext } from "@ryde/redux";
import {
  companyIdSelector,
  generateGuid,
  ICustomField,
  INeighborAreaSettings,
  isInnerItemEditingAtom,
  TNeighborAreaSettingsState,
  TNewNeighborAreaSettings,
  unique,
} from "@ryde/shared";

import { MultiSelectBoundFormless } from "../../../controls";
import { PRIVATE_AREA_IDS_FORM_FIELD_NAME } from "./constants";
import { NeighborAreasSettingsAddItem } from "./neighbor-areas-settings-add-item";
import { NeighborAreasSettingsGroupTitle } from "./neighbor-areas-settings-group-title";
import { NeighborAreasSettingsItem } from "./neighbor-areas-settings-item";
import { NeighborAreasTemplateContext } from "./neighbor-areas-template-context";
import { NeighborAreasTemplateWrapperStyled } from "./neighbor-areas.styled";
import {
  filterNeighborAreasSettingsToShow,
  getIsNeighborAreasSettingsEmpty,
} from "./neighbor-areas.utils";

export const NeighborAreasTemplate: FC<ICustomField> = ({ name }) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const {
    id,
    data: { dto, isEdit },
  } = useCrudEditContext();
  const companyId = useRecoilValue(companyIdSelector);
  const companyScopeKey = String(companyId || "none");
  const [isInitialSet, setIsInitialSet] = useState(false);
  const [areaIds, setAreaIds] = useState<number[]>([]);
  const [neighborAreasSettings, setNeighborAreasSettings] = useState<
    TNeighborAreaSettingsState[]
  >([]);
  // Used for avoid race condition in use effects
  const neighborAreasSettingsRef = useRef<INeighborAreaSettings[]>([]);
  const [editingCount, setEditingCount] = useState(0);
  const setIsInnerItemEditing = useSetRecoilState(isInnerItemEditingAtom);

  const handleInnerEditStart = useEvent(() => {
    setEditingCount(prev => prev + 1);
  });

  const handleInnerEditEnd = useEvent(() => {
    setEditingCount(prev => Math.max(0, prev - 1));
  });

  useEffect(() => {
    setIsInnerItemEditing(editingCount > 0);
  }, [editingCount, setIsInnerItemEditing]);

  useEffect(() => {
    return () => {
      setIsInnerItemEditing(false);
    };
  }, [setIsInnerItemEditing]);

  const neighborAreasSettingsFilteredGroupedToShow = chain(
    neighborAreasSettings,
  )
    .filter(filterNeighborAreasSettingsToShow)
    .filter(s => s.companyId === companyId)
    .groupBy("areaId")
    .value();

  const handleAreaIdsChange = useEvent((newAreaIds: number[]) => {
    setAreaIds(newAreaIds);
  });

  const onSettingAdd = useEvent(
    (neighborAreaSettings: TNewNeighborAreaSettings): void => {
      setNeighborAreasSettings(prevState => {
        const newState = [...prevState];

        const neighborAreaIndex = newState.findIndex(
          prevSettings =>
            neighborAreaSettings.areaId === prevSettings.areaId &&
            isNil(prevSettings.direction) &&
            isNil(prevSettings.toTime) &&
            isNil(prevSettings.fromTime) &&
            !prevSettings.dayGroupIds?.length &&
            !prevSettings.dayTypeIds?.length,
        );

        if (
          neighborAreaIndex >= 0 &&
          getIsNeighborAreasSettingsEmpty(
            newState.at(neighborAreaIndex) as
              INeighborAreaSettings | TNewNeighborAreaSettings,
          )
        ) {
          return newState.with(neighborAreaIndex, {
            ...neighborAreaSettings,
            companyId: neighborAreaSettings.companyId || companyId,
            tmpId: generateGuid(),
          });
        }

        newState.push({
          ...neighborAreaSettings,
          companyId: neighborAreaSettings.companyId || companyId,
          tmpId: generateGuid(),
        });
        return newState;
      });
    },
  );

  const onSettingEdit = useEvent(
    ({
      tmpId,
      neighborAreaSettings,
    }: {
      tmpId: string;
      neighborAreaSettings: TNeighborAreaSettingsState;
    }): void => {
      setNeighborAreasSettings(prevState => {
        const indexNeighborAreaSettingToUpdate = prevState.findIndex(
          prevNeighborAreaSetting => prevNeighborAreaSetting.tmpId === tmpId,
        );
        const result = prevState.with(indexNeighborAreaSettingToUpdate, {
          ...neighborAreaSettings,
          companyId: neighborAreaSettings.companyId || companyId,
        });

        return result;
      });
    },
  );

  const onSettingRemove = useEvent((tmpId: string): void => {
    setNeighborAreasSettings(prevState => {
      const settingsToRemoveIndex = prevState.findIndex(
        prevStateSetting => prevStateSetting.tmpId === tmpId,
      );
      const settingsToRemove = prevState[settingsToRemoveIndex];

      const isLastSettingWithThisAreaId =
        prevState.filter(
          prevAreaSettings =>
            prevAreaSettings.areaId === settingsToRemove?.areaId,
        ).length === 1;

      if (isLastSettingWithThisAreaId) {
        const resetSettings: Partial<INeighborAreaSettings> = {
          areaId: settingsToRemove?.areaId,
          companyId: settingsToRemove?.companyId || companyId,
        };

        if ("id" in settingsToRemove) {
          resetSettings.id = settingsToRemove?.id;
        }

        return prevState.with(settingsToRemoveIndex, {
          ...resetSettings,
          tmpId: settingsToRemove.tmpId,
        } as TNeighborAreaSettingsState);
      }

      return prevState.filter(
        prevNeighborAreaSetting => prevNeighborAreaSetting.tmpId !== tmpId,
      );
    });
  });

  // init local and form states
  useEffect(() => {
    if (dto) {
      const neighborAreasFromEntity: INeighborAreaSettings[] = get(
        dto,
        name,
        [],
      );
      const neighborAreasStamped = neighborAreasFromEntity.map(n => ({
        ...n,
        companyId: n.companyId || companyId,
      }));
      setAreaIds(
        unique(neighborAreasStamped?.map(({ areaId }) => areaId) ?? []),
      );
      setNeighborAreasSettings(
        neighborAreasStamped.map(neighborAreaFromEntity => ({
          ...neighborAreaFromEntity,
          tmpId: generateGuid(),
        })),
      );
      neighborAreasSettingsRef.current = neighborAreasStamped;
      setIsInitialSet(true);
    }
  }, [companyId, dto, getValues, name]);

  useEffect(() => {
    setValue(name, neighborAreasSettings, {
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [name, neighborAreasSettings, setValue]);

  useEffect(() => {
    if (isEdit && !isInitialSet) {
      return;
    }

    // is new areaId was add
    const areaIdsWereSet = neighborAreasSettingsRef.current.map(
      neighborAreaSettings => neighborAreaSettings.areaId,
    );
    const newIds = difference(areaIds, areaIdsWereSet);
    const removedIds = difference(areaIdsWereSet, areaIds);

    if (newIds.length) {
      setNeighborAreasSettings(prevState => [
        ...prevState,
        ...newIds.map(areaId => ({
          areaId,
          companyId,
          tmpId: generateGuid(),
        })),
      ]);
      neighborAreasSettingsRef.current = [
        ...(neighborAreasSettingsRef?.current ?? []),
        ...newIds.map(areaId => ({ areaId, companyId })),
      ] as INeighborAreaSettings[];
      return;
    }

    if (removedIds.length) {
      const removedIdsSet = new Set(removedIds);
      setNeighborAreasSettings(prevState => {
        const result = prevState.filter(
          ({ areaId }) => !removedIdsSet.has(areaId),
        );

        return result;
      });

      neighborAreasSettingsRef.current =
        neighborAreasSettingsRef.current.filter(
          ({ areaId }) => !removedIds.includes(areaId),
        );
      return;
    }
  }, [areaIds, companyId, isEdit, isInitialSet]);

  return (
    <NeighborAreasTemplateWrapperStyled>
      <div
        className="NeighborAreasSettings__container"
        data-inner-editing={editingCount > 0 ? "true" : undefined}
      >
        <div className="NeighborAreasSettings-AreaSelect display-mode">
          <MultiSelectBoundFormless
            key={`neighbor-area-ms-${companyScopeKey}-${id ?? "new"}`}
            name={PRIVATE_AREA_IDS_FORM_FIELD_NAME}
            labelTranslated={t("area.formLabel.areaIds")}
            formAttributes={{
              dropItemsSource: "area",
              excludeIds: id ? [id] : [],
              dropItemsDisableAdd: true,
            }}
            value={areaIds}
            onChange={handleAreaIdsChange}
          />
        </div>
        <NeighborAreasTemplateContext.Provider
          value={{
            fieldName: name,
            onAdd: onSettingAdd,
            onEdit: onSettingEdit,
            onRemove: onSettingRemove,
            availableAreas: areaIds,
            companyId,
            onInnerEditStart: handleInnerEditStart,
            onInnerEditEnd: handleInnerEditEnd,
          }}
        >
          {Boolean(
            Object.keys(neighborAreasSettingsFilteredGroupedToShow).length,
          ) && (
            <section className="NeighborAreasSettings-CollapseSection">
              <header className="NeighborAreasSettings-Header display-mode">
                <span>{startCase(t("area.single"))}</span>
                <span>{t("general.FROM")}</span>
                <span>{t("general.TO")}</span>
                <span>{t("general.DIRECTION")}</span>
                <span />
              </header>
              <div className="NeighborAreasSettings-List">
                {Object.keys(neighborAreasSettingsFilteredGroupedToShow).map(
                  (areaId, neighborAreaDetailsIndex) => (
                    <Fragment key={`fragment-${companyScopeKey}-${areaId}`}>
                      <NeighborAreasSettingsGroupTitle
                        key={`group-title-${companyScopeKey}-${areaId}`}
                        areaId={Number(areaId)}
                      />
                      {neighborAreasSettingsFilteredGroupedToShow[areaId].map(
                        (neighborAreaDetails, index) => (
                          <NeighborAreasSettingsItem
                            key={`neighbor-area-item-${companyScopeKey}-${neighborAreaDetailsIndex}-${areaId}-${index}`}
                            neighborAreaDetails={neighborAreaDetails}
                            neighborAreaDetailsIndex={neighborAreaDetailsIndex}
                          />
                        ),
                      )}
                    </Fragment>
                  ),
                )}
              </div>
            </section>
          )}
          {Boolean(areaIds?.length) && (
            <NeighborAreasSettingsAddItem key={`add-item-${companyScopeKey}`} />
          )}
        </NeighborAreasTemplateContext.Provider>
      </div>
    </NeighborAreasTemplateWrapperStyled>
  );
};
